import loadGoogleMapsApi from 'load-google-maps-api'

export interface SuggestedOption {
  label: string
  placeId: string
  value: string
}

export interface GooglePlaceLocation {
  lat: number
  lng: number
}

interface AutocompleteService {
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement
  Map: typeof google.maps.Map
  PlacesServiceStatus: typeof google.maps.places.PlacesServiceStatus
  autoCompleteService: google.maps.places.AutocompleteService
  placesService: google.maps.places.PlacesService
}

// TODO: make import from env or window
let googlePlacesKey = ''

export const fetchCities = async (
  country: string,
  value: string,
  googlePlacesApiKey: string,
): Promise<SuggestedOption[]> => {
  googlePlacesKey = googlePlacesApiKey
  const predictions = await getPlacePredictions({
    input: value || ' ',
    types: ['(regions)'],
    componentRestrictions: { country: country.toLowerCase() },
  })
  return predictions.map(predictionToOption)
}

export const fetchAddresses = async (value: string, googlePlacesApiKey: string): Promise<SuggestedOption[]> => {
  googlePlacesKey = googlePlacesApiKey
  const predictions = await getPlacePredictions({
    input: value || ' ',
    types: ['address'],
  })
  return predictions.map(predictionToOption)
}

export const getAddressDetails = async (
  placeId: string,
  googlePlacesApiKey: string,
): Promise<google.maps.GeocoderAddressComponent[]> => {
  return (
    (
      await getPlaceDetails(
        {
          placeId,
          fields: ['address_components'],
        },
        googlePlacesApiKey,
      )
    ).address_components || []
  )
}

export const getCoordinates = async (placeId: string, googlePlacesApiKey: string): Promise<GooglePlaceLocation> => {
  const location = (
    await getPlaceDetails(
      {
        placeId,
        fields: ['geometry.location'],
      },
      googlePlacesApiKey,
    )
  ).geometry?.location
  return location ? { lat: location.lat(), lng: location.lng() } : { lat: 0, lng: 0 }
}

async function getPlacePredictions(
  request: google.maps.places.AutocompletionRequest,
): Promise<google.maps.places.AutocompletePrediction[]> {
  const { autoCompleteService, PlacesServiceStatus } = await getAutoCompleteService()
  return new Promise((resolve, reject) => {
    void autoCompleteService.getPlacePredictions(request, (predictions, status) => {
      switch (status) {
        case PlacesServiceStatus.OK:
          return resolve(predictions || [])
        case PlacesServiceStatus.ZERO_RESULTS:
        case PlacesServiceStatus.NOT_FOUND:
          return resolve([])
        default:
          return reject(new Error(status))
      }
    })
  })
}

export async function getPlaceDetails(
  request: google.maps.places.PlaceDetailsRequest,
  googlePlacesApiKey: string,
): Promise<google.maps.places.PlaceResult> {
  googlePlacesKey = googlePlacesApiKey
  const { placesService, PlacesServiceStatus } = await getAutoCompleteService()
  return new Promise((resolve, reject) => {
    placesService.getDetails(request, (predictions, status) => {
      switch (status) {
        case PlacesServiceStatus.OK:
          return resolve(predictions || {})
        case PlacesServiceStatus.ZERO_RESULTS:
        case PlacesServiceStatus.NOT_FOUND:
          return resolve({})
        default:
          return reject(new Error(status))
      }
    })
  })
}

export async function getAutoCompleteService(googlePlacesApiKey?: string): Promise<AutocompleteService> {
  if (googlePlacesApiKey) googlePlacesKey = googlePlacesApiKey
  if (!googlePlacesKey) return Promise.reject(new Error())

  const { places, Map, marker } = await loadGoogleMapsApi({ key: googlePlacesKey, libraries: ['places', 'marker'] })
  const AdvancedMarkerElement = marker.AdvancedMarkerElement
  const { AutocompleteService, PlacesService, PlacesServiceStatus } = places
  const autoCompleteService = new AutocompleteService()
  const placesService = new PlacesService(document.createElement('div'))
  return { autoCompleteService, placesService, PlacesServiceStatus, Map, AdvancedMarkerElement }
}

function predictionToOption(prediction: google.maps.places.AutocompletePrediction): SuggestedOption {
  return {
    label: prediction.description,
    value: prediction.structured_formatting.main_text,
    placeId: prediction.place_id,
  }
}
